import React, { useState, useEffect } from 'react';
import FormControl from '@material-ui/core/FormControl';
import Autocomplete, {
  AutocompleteChangeReason,
} from '@material-ui/lab/Autocomplete';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useDebounce } from 'use-debounce';
import {
  EntityFilterQuery,
  QueryEntitiesRequest,
} from '@backstage/catalog-client';
import { TextField } from '@material-ui/core';
import { Entity } from '@backstage/catalog-model';

export interface EntryType {
  title: string;
  entity: Entity;
}

export const UserInput = (props: {
  onChange: (newValue: Entity) => void;
  label?: string;
  helperText?: string;
  required?: boolean;
  fullWidth?: boolean;
  error?: boolean;
  id?: string;
  value?: string;
  usersToFilter?: Entity[];
}) => {
  const catalogApi = useApi(catalogApiRef);
  const catalogFilter = [{ kind: ['User'] }] as EntityFilterQuery;
  const [value, setValue] = useState<string | EntryType | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [debouncedInputValue] = useDebounce(inputValue, 500);
  const [entries, setEntries] = useState<EntryType[]>([]);
  const [loadingEntries, setLoadingEntries] = useState(false);

  useEffect(() => {
    async function startFetching() {
      setLoadingEntries(true);
      // The catalog matches the term as one piece, and a display name reads
      // "Schulz, Lion [...]" - "Lion Schulz" would find nobody. So only the
      // longest word goes to the catalog, it narrows down the most, and the
      // users it finds must have every word somewhere, in any order.
      const words = inputValue
        .toLocaleLowerCase('en-US')
        .split(/[\s,]+/)
        .filter(word => word.length > 0);
      const longestWord = words.reduce(
        (longest, word) => (word.length > longest.length ? word : longest),
        '',
      );
      const queryRequest: QueryEntitiesRequest = {
        filter: catalogFilter,
        limit: 50,
        fullTextFilter: {
          term: longestWord,
          fields: [
            'metadata.title',
            'spec.profile.userPrincipalName',
            'spec.profile.email',
          ],
        },
        orderFields: { field: 'metadata.title', order: 'asc' },
      };
      const result = await catalogApi.queryEntities(queryRequest);
      const entityRefs = result.items;
      const entries = filterEntries(
        entityRefs.filter(entity => hasEveryWord(entity, words)),
      );

      if (!ignore) {
        setEntries(entries);
      }
      setLoadingEntries(false);
    }

    let ignore = false;

    if (debouncedInputValue || inputValue == '') {
      startFetching();
    }
    return () => {
      ignore = true;
    };
  }, [debouncedInputValue]);

  function handleOnChange(
    value: string | EntryType | null,
    reason: AutocompleteChangeReason,
  ) {
    if (reason !== 'select-option') {
      return;
    }

    setValue(null);
    setInputValue('');
    props.onChange((value as EntryType).entity);
  }

  function handleInputChange(value: string) {
    setLoadingEntries(true);
    setEntries([]);
    setInputValue(value);
  }

  return (
    <FormControl required={props.required} fullWidth={props.fullWidth}>
      <Autocomplete<EntryType | string | null>
        options={entries}
        getOptionLabel={(option: any) => (option?.title ? option.title : '')}
        // The matching is done, on the UPN and the email as well; the stock
        // filter would match the label - the display name - once more and
        // drop every user found by the UPN or the email alone.
        filterOptions={options => options}
        id={props.id}
        loading={loadingEntries}
        value={value}
        inputValue={inputValue}
        onInputChange={(_, value) => handleInputChange(value)}
        onChange={(_, value, reason) => handleOnChange(value, reason)}
        renderInput={params => (
          <TextField
            {...params}
            label={props.label ? props.label : 'User'}
            margin="dense"
            helperText={
              props.helperText
                ? props.helperText
                : 'Select a User you want to add'
            }
            FormHelperTextProps={{ margin: 'dense', style: { marginLeft: 0 } }}
            variant="outlined"
            required={props.required}
            InputProps={params.InputProps}
            error={props.error}
          />
        )}
      />
    </FormControl>
  );

  function filterEntries(entityRefs: any): EntryType[] {
    const filteredRefs = entityRefs?.filter((entity: any) => {
      const userPrincipalName = (
        entity.spec!.profile as any
      ).userPrincipalName.toLowerCase();
      if (!props.usersToFilter?.includes(userPrincipalName)) {
        return entity;
      }
      return;
    });
    const entities = filteredRefs
      ?.map((it: Entity) => toEntry(it))
      .sort((a: EntryType, b: EntryType) => (a.title < b.title ? -1 : 1));
    return entities || [];
  }

  function hasEveryWord(entity: Entity, words: string[]): boolean {
    const profile = entity.spec?.profile as any;
    const searchable = [
      entity.metadata.title,
      profile?.userPrincipalName,
      profile?.email,
    ]
      .filter(Boolean)
      .join(' ')
      .toLocaleLowerCase('en-US');
    return words.every(word => searchable.includes(word));
  }

  function toEntry(entity: Entity): EntryType {
    return {
      title: (entity.spec!.profile as any).displayName!,
      entity: entity,
    };
  }
};
